// Talmia 2.0 · Convert Call → Lead (AI-assisted, human-in-the-loop).
// Slide-over launched from the Calls page "+ Lead" action on an unmatched call.
// Prefill is instant (mapped from the call's existing AI tags); company /
// delivery-area / products are filled a moment later by the Gemini extractor.
// A live duplicate check (email · phone · company) points agents to existing
// records. The agent reviews/edits everything and explicitly saves.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  X, Sparkles, Loader2, UserPlus, AlertTriangle, PhoneOutgoing, PhoneIncoming,
  Clock, ArrowRight, CheckCircle2, Plus,
} from "lucide-react";

const AiBadge = () => (
  <span data-testid="ai-badge" title="AI-inferred — please review"
    style={{
      display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6,
      background: "var(--t2-teal-dim, rgba(45,212,191,.12))",
      border: "1px solid var(--t2-teal, #2dd4bf)", color: "var(--t2-teal, #2dd4bf)",
      borderRadius: 5, padding: "1px 5px", fontSize: 9, fontWeight: 700,
      letterSpacing: ".06em", textTransform: "uppercase", verticalAlign: "middle",
    }} className="t2-mono">
    <Sparkles className="w-2.5 h-2.5" /> AI
  </span>
);

const Label = ({ children, ai, htmlFor }) => (
  <label htmlFor={htmlFor} className="t2-mono"
    style={{
      display: "block", fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
      color: "var(--t2-brass)", fontWeight: 700, marginBottom: 5,
    }}>
    {children}{ai && <AiBadge />}
  </label>
);

export default function ConvertCallToLead({ callId, onClose, onSaved }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading]   = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [callMeta, setCallMeta] = useState(null);
  const [aiFields, setAiFields] = useState(new Set());   // fields marked AI-inferred
  const [ams, setAms]           = useState([]);
  const [stages, setStages]     = useState([]);
  const [dups, setDups]         = useState([]);
  const [dupBusy, setDupBusy]   = useState(false);

  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", company: "",
    delivery_area: "", products: [], source: "Inbound call",
    owner: "", first_note: "", stage_key: "new_lead",
  });
  const [productDraft, setProductDraft] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markAi = (keys) => setAiFields((prev) => { const n = new Set(prev); keys.forEach((k) => n.add(k)); return n; });
  const clearAi = (k) => setAiFields((prev) => { if (!prev.has(k)) return prev; const n = new Set(prev); n.delete(k); return n; });

  // ── Load prefill + reference data on mount ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pf, amR, stR] = await Promise.all([
          api.get(`/calls/${callId}/lead-prefill`),
          api.get(`/me/account-managers`).catch(() => ({ data: { account_managers: [] } })),
          api.get(`/crm/lead-stages`).catch(() => ({ data: { stages: [] } })),
        ]);
        if (cancelled) return;
        const p = pf.data.prefill || {};
        setCallMeta(pf.data.call || null);
        setForm((f) => ({
          ...f, ...p,
          owner: (user?.name || "").trim(),   // defaults to the converting agent
          stage_key: "new_lead",
        }));
        setAiFields(new Set(pf.data.ai_fields || []));
        setAms(amR.data.account_managers || []);
        const st = stR.data.stages || stR.data.items || [];
        setStages(Array.isArray(st) ? st : []);
      } catch (e) {
        toast.error("Couldn't load call details for conversion.");
        onClose?.();
        return;
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Fire the AI extractor (company / area / products) — fills in a moment.
      setExtracting(true);
      try {
        const { data } = await api.post(`/calls/${callId}/lead-extract`);
        if (cancelled) return;
        const ex = data.ai_extract || {};
        setForm((f) => {
          const next = { ...f };
          const marked = [];
          if (ex.company && !f.company) { next.company = ex.company; marked.push("company"); }
          if (ex.delivery_area && !f.delivery_area) { next.delivery_area = ex.delivery_area; marked.push("delivery_area"); }
          if (ex.first_name && !f.first_name) { next.first_name = ex.first_name; marked.push("first_name"); }
          if (ex.last_name && !f.last_name) { next.last_name = ex.last_name; marked.push("last_name"); }
          if (Array.isArray(ex.products) && ex.products.length && (!f.products || f.products.length === 0)) {
            next.products = ex.products; marked.push("products");
          }
          if (marked.length) markAi(marked);
          return next;
        });
      } catch { /* degrade gracefully to agent-filled */ }
      finally { if (!cancelled) setExtracting(false); }
    })();
    return () => { cancelled = true; };
  }, [callId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live duplicate check (debounced) ──────────────────────────────
  const dupKey = `${form.email}|${form.phone}|${form.first_name} ${form.last_name}|${form.company}`;
  useEffect(() => {
    if (loading) return;
    const email = form.email.trim();
    const phone = form.phone.trim();
    const name = `${form.first_name} ${form.last_name}`.trim();
    const company = form.company.trim();
    if (!email && !phone && name.length < 2 && company.length < 3) { setDups([]); return; }
    let cancelled = false;
    setDupBusy(true);
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams();
        if (email) qs.set("email", email);
        if (phone) qs.set("phone", phone);
        if (name) qs.set("name", name);
        if (company) qs.set("company", company);
        const { data } = await api.get(`/leads/dup-check?${qs.toString()}`);
        if (!cancelled) setDups(data.matches || []);
      } catch { if (!cancelled) setDups([]); }
      finally { if (!cancelled) setDupBusy(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dupKey, loading]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addProduct = () => {
    const v = productDraft.trim();
    if (!v) return;
    if (!form.products.includes(v)) setField("products", [...form.products, v]);
    setProductDraft("");
  };
  const removeProduct = (p) => { setField("products", form.products.filter((x) => x !== p)); clearAi("products"); };

  const canSave = useMemo(
    () => form.first_name.trim() && (form.phone.trim() || form.email.trim()) && !saving,
    [form.first_name, form.phone, form.email, saving],
  );

  const doSave = async () => {
    if (!canSave) {
      if (!form.first_name.trim()) toast.error("First name is required.");
      else toast.error("A phone number or email is required.");
      return;
    }
    setSaving(true);
    try {
      const note = form.first_note.trim();
      const productLine = form.products.length ? `Products of interest: ${form.products.join(", ")}` : "";
      const finalNote = [note, productLine].filter(Boolean).join("\n\n");
      const { data } = await api.post(`/calls/${callId}/convert-to-lead`, {
        first_name:    form.first_name.trim(),
        last_name:     form.last_name.trim(),
        email:         form.email.trim(),
        phone:         form.phone.trim(),
        company:       form.company.trim(),
        delivery_area: form.delivery_area.trim(),
        source:        form.source,
        owner:         form.owner.trim(),
        first_note:    finalNote,
        stage_key:     form.stage_key,
      });
      if (data.created === false) {
        toast.success("Matched an existing record — call attached to them.", {
          action: { label: "Open", onClick: () => navigate(`/crm/persons/${data.person_id}`) },
        });
      } else {
        toast.success(`Lead created · ${form.first_name} ${form.last_name}`.trim(), {
          action: { label: "View in pipeline", onClick: () => navigate(`/leads`) },
        });
      }
      onSaved?.(callId, data.person_id);
      onClose?.();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Couldn't create the lead."));
    } finally { setSaving(false); }
  };

  const isOut = callMeta?.direction === "Outbound";
  const DirIcon = isOut ? PhoneOutgoing : PhoneIncoming;
  const fmtDur = (s) => {
    if (s == null) return "—";
    const m = Math.floor(s / 60), r = s % 60;
    return m ? `${m}m ${r}s` : `${r}s`;
  };

  return (
    <div data-testid="convert-lead-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 70, display: "flex", justifyContent: "flex-end" }}>
      <div data-testid="convert-lead-panel"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 520, maxWidth: "94vw", height: "100%", overflowY: "auto",
          background: "var(--t2-surface2)", borderLeft: "1px solid var(--t2-border)",
          boxShadow: "-20px 0 60px rgba(0,0,0,.5)", animation: "t2SlideIn .22s ease",
        }} className="t2-scroll">
        <style>{`@keyframes t2SlideIn{from{transform:translateX(24px);opacity:.4}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ position: "sticky", top: 0, zIndex: 2, background: "var(--t2-surface2)", borderBottom: "1px solid var(--t2-border)", padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div className="t2-page-eyebrow" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <UserPlus className="w-3 h-3" /> New Lead from Call
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--t2-white)", margin: "5px 0 0" }}>
                Convert to lead
              </h2>
            </div>
            <button onClick={onClose} data-testid="convert-lead-close"
              style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer", padding: 4 }}>
              <X className="w-5 h-5" />
            </button>
          </div>
          {callMeta && (
            <div className="t2-mono" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 11, color: "var(--t2-grey2)", flexWrap: "wrap" }}>
              {callMeta.direction && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: isOut ? "var(--t2-orange)" : "var(--t2-teal)" }}>
                  <DirIcon className="w-3 h-3" /> {callMeta.direction}
                </span>
              )}
              {callMeta.agent && <span>· {callMeta.agent}</span>}
              {callMeta.duration != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Clock className="w-3 h-3" /> {fmtDur(callMeta.duration)}</span>}
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: "var(--t2-grey3)" }} data-testid="convert-lead-loading">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading call…
          </div>
        ) : (
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Duplicate banner */}
            <DupBanner dups={dups} busy={dupBusy} onOpen={(pid) => navigate(`/crm/persons/${pid}`)} />

            {extracting && (
              <div className="t2-mono" data-testid="convert-lead-extracting"
                style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--t2-teal)" }}>
                <Loader2 className="w-3 h-3 animate-spin" /> AI reading the call for company & delivery area…
              </div>
            )}

            {/* Name */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label htmlFor="cl-first" ai={aiFields.has("first_name")}>First name *</Label>
                <input id="cl-first" className="t2-input" data-testid="convert-lead-first-name"
                  value={form.first_name} onChange={(e) => { setField("first_name", e.target.value); clearAi("first_name"); }}
                  style={{ width: "100%" }} />
              </div>
              <div>
                <Label htmlFor="cl-last" ai={aiFields.has("last_name")}>Last name</Label>
                <input id="cl-last" className="t2-input" data-testid="convert-lead-last-name"
                  value={form.last_name} onChange={(e) => { setField("last_name", e.target.value); clearAi("last_name"); }}
                  style={{ width: "100%" }} />
              </div>
            </div>

            {/* Contact */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label htmlFor="cl-phone">Phone</Label>
                <input id="cl-phone" className="t2-input" data-testid="convert-lead-phone"
                  value={form.phone} onChange={(e) => setField("phone", e.target.value)}
                  placeholder="Phone number" style={{ width: "100%" }} />
              </div>
              <div>
                <Label htmlFor="cl-email">Email</Label>
                <input id="cl-email" className="t2-input" data-testid="convert-lead-email"
                  value={form.email} onChange={(e) => setField("email", e.target.value)}
                  placeholder="Optional" style={{ width: "100%" }} />
              </div>
            </div>
            <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey4)", marginTop: -8 }}>
              Phone-only leads are allowed — email is optional here.
            </div>

            {/* Company + area */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label htmlFor="cl-company" ai={aiFields.has("company")}>Company</Label>
                <input id="cl-company" className="t2-input" data-testid="convert-lead-company"
                  value={form.company} onChange={(e) => { setField("company", e.target.value); clearAi("company"); }}
                  style={{ width: "100%" }} />
              </div>
              <div>
                <Label htmlFor="cl-area" ai={aiFields.has("delivery_area")}>Delivery area / postcode</Label>
                <input id="cl-area" className="t2-input" data-testid="convert-lead-area"
                  value={form.delivery_area} onChange={(e) => { setField("delivery_area", e.target.value); clearAi("delivery_area"); }}
                  style={{ width: "100%" }} />
              </div>
            </div>

            {/* Products of interest */}
            <div>
              <Label ai={aiFields.has("products")}>Products of interest</Label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }} data-testid="convert-lead-products">
                {form.products.map((p) => (
                  <span key={p} className="t2-mono" data-testid={`convert-lead-product-${p}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--t2-surface3)", border: "1px solid var(--t2-border2)", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "var(--t2-grey1)" }}>
                    {p}
                    <button onClick={() => removeProduct(p)} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer", padding: 0, display: "flex" }}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {form.products.length === 0 && <span className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey4)" }}>None yet</span>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="t2-input" data-testid="convert-lead-product-input"
                  value={productDraft} onChange={(e) => setProductDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProduct(); } }}
                  placeholder="Add a product…" style={{ flex: 1 }} />
                <button onClick={addProduct} data-testid="convert-lead-product-add"
                  style={{ background: "var(--t2-surface3)", border: "1px solid var(--t2-border2)", color: "var(--t2-grey1)", borderRadius: 7, padding: "0 12px", cursor: "pointer", display: "flex", alignItems: "center" }}>
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Source · Owner · Stage */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label htmlFor="cl-source">Source</Label>
                <input id="cl-source" className="t2-input" data-testid="convert-lead-source"
                  value={form.source} onChange={(e) => setField("source", e.target.value)} style={{ width: "100%" }} />
              </div>
              <div>
                <Label htmlFor="cl-owner">Owner</Label>
                <select id="cl-owner" className="t2-input" data-testid="convert-lead-owner"
                  value={form.owner} onChange={(e) => setField("owner", e.target.value)} style={{ width: "100%" }}>
                  {form.owner && !ams.some((a) => a.name === form.owner) && (
                    <option value={form.owner}>{form.owner}</option>
                  )}
                  {ams.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
                  <option value="">Unassigned</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="cl-stage">Pipeline stage</Label>
              <select id="cl-stage" className="t2-input" data-testid="convert-lead-stage"
                value={form.stage_key} onChange={(e) => setField("stage_key", e.target.value)} style={{ width: "100%" }}>
                {stages.length === 0 && <option value="new_lead">New lead</option>}
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label || s.name || s.key}</option>)}
              </select>
            </div>

            {/* First note */}
            <div>
              <Label htmlFor="cl-note" ai={aiFields.has("first_note")}>First note (from call summary)</Label>
              <textarea id="cl-note" className="t2-input" data-testid="convert-lead-note"
                value={form.first_note} onChange={(e) => { setField("first_note", e.target.value); clearAi("first_note"); }}
                rows={5} style={{ width: "100%", resize: "vertical", lineHeight: 1.45 }} />
            </div>
          </div>
        )}

        {/* Footer actions */}
        {!loading && (
          <div style={{ position: "sticky", bottom: 0, background: "var(--t2-surface2)", borderTop: "1px solid var(--t2-border)", padding: "14px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} data-testid="convert-lead-cancel"
              style={{ background: "none", border: "1px solid var(--t2-border2)", color: "var(--t2-grey2)", borderRadius: 8, padding: "9px 16px", cursor: "pointer", fontWeight: 600 }}>
              Cancel
            </button>
            <button onClick={doSave} disabled={!canSave} data-testid="convert-lead-save"
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                background: canSave ? "var(--t2-orange)" : "var(--t2-surface3)",
                border: "none", color: canSave ? "#1a1205" : "var(--t2-grey4)",
                borderRadius: 8, padding: "9px 18px", fontWeight: 800,
                cursor: canSave ? "pointer" : "not-allowed",
                boxShadow: canSave ? "0 6px 18px rgba(255,138,0,.35)" : "none",
              }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {saving ? "Saving…" : "Save lead"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DupBanner({ dups, busy, onOpen }) {
  if (busy && dups.length === 0) {
    return (
      <div className="t2-mono" data-testid="convert-lead-dup-checking"
        style={{ fontSize: 11, color: "var(--t2-grey3)", display: "flex", alignItems: "center", gap: 6 }}>
        <Loader2 className="w-3 h-3 animate-spin" /> Checking for existing records…
      </div>
    );
  }
  if (dups.length === 0) {
    return (
      <div className="t2-mono" data-testid="convert-lead-dup-clear"
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--t2-teal)", background: "var(--t2-teal-dim, rgba(45,212,191,.08))", border: "1px solid var(--t2-teal, #2dd4bf)", borderRadius: 8, padding: "8px 10px" }}>
        <CheckCircle2 className="w-3.5 h-3.5" /> No existing record found — this will be a brand-new lead.
      </div>
    );
  }
  const reasonLabel = { email: "same email", phone: "same phone", name: "similar name", company: "same company" };
  return (
    <div data-testid="convert-lead-dup-warning"
      style={{ background: "rgba(245,158,11,.08)", border: "1px solid var(--t2-warn, #f59e0b)", borderRadius: 8, padding: "10px 12px" }}>
      <div className="t2-mono" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--t2-warn, #f59e0b)", fontWeight: 700, marginBottom: 8 }}>
        <AlertTriangle className="w-3.5 h-3.5" /> {dups.length} possible existing {dups.length === 1 ? "record" : "records"}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {dups.slice(0, 4).map((d) => (
          <button key={d.id} onClick={() => onOpen(d.id)} data-testid={`convert-lead-dup-${d.id}`}
            style={{ textAlign: "left", background: "var(--t2-surface)", border: "1px solid var(--t2-border2)", borderRadius: 7, padding: "7px 9px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 12.5, color: "var(--t2-white)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {d.name || d.full_name || d.email || "Contact"}
              </div>
              <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }}>
                {[d.company, d.email, d.phone].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            <span className="t2-mono" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, color: "var(--t2-warn, #f59e0b)", whiteSpace: "nowrap" }}>
              {reasonLabel[d.match_on] || d.match_on} <ArrowRight className="w-3 h-3" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
